import type { Decimalish, ProjectionRow, SimulationType } from '@/services/inventorySimulationService';

export type Granularity = 'week' | 'month';

export interface PivotPeriod {
  key: string;
  label: string;
  periodStart: string;
  periodEnd: string;
  startingStock: number;
  confirmedReceipts: number;
  delayedReceipts: number;
  suggestedReceipts: number;
  projectedDemand: number;
  endingStock: number;
  safetyStock: number;
  belowSafety: boolean;
  stockout: boolean;
  inProtectionPeriod: boolean;
}

/** Suma cantidades de pedidos en transito con fecha anterior a la semana actual (type: antiguo). */
export function delayedReceiptsFromDetails(
  details: ProjectionRow['receipts_details'] | null | undefined,
): number {
  if (!details?.length) return 0;
  return details
    .filter((entry) => entry?.type === 'antiguo')
    .reduce((acc, entry) => acc + toNum(entry.quantity), 0);
}

export function toNum(value: Decimalish | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

const qtyFormatter = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export function formatQty(value: Decimalish): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return qtyFormatter.format(n);
}

function parseIsoDate(value: string): Date {
  const [y, m, d] = value.split('-').map((p) => Number(p));
  return new Date(y, (m || 1) - 1, d || 1);
}

const periodAnchorDateFormatterCache = new Map<string, Intl.DateTimeFormat>();

function periodAnchorDateFormatter(locale: string): Intl.DateTimeFormat {
  const key = locale || 'es-ES';
  let formatter = periodAnchorDateFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(key, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    periodAnchorDateFormatterCache.set(key, formatter);
  }
  return formatter;
}

/** Fecha de anclaje del bucket (lunes en vista semanal; primer lunes del mes en vista mensual). */
export function formatPeriodAnchorDate(periodStart: string, locale = 'es-ES'): string {
  return periodAnchorDateFormatter(locale).format(parseIsoDate(periodStart));
}

/** Semana ISO 8601 (lunes como primer dia). */
export function isoWeekLabel(periodStart: string): string {
  const date = parseIsoDate(periodStart);
  const target = new Date(date.getTime());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstDayNr = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNr + 3);
  const week = 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
  return `S${String(week).padStart(2, '0')} ${target.getFullYear()}`;
}

const monthFormatter = new Intl.DateTimeFormat('es-ES', { month: 'short', year: 'numeric' });

function monthKey(periodStart: string): string {
  return periodStart.slice(0, 7);
}

function monthLabel(periodStart: string): string {
  return monthFormatter.format(parseIsoDate(periodStart));
}

export function resolveProtectionDays(
  protectionPeriodDays: number | null | undefined,
  leadTimeDays: number | null | undefined,
  reviewPeriodDays: number | null | undefined,
): number {
  if (protectionPeriodDays != null) return protectionPeriodDays;
  return (leadTimeDays ?? 0) + (reviewPeriodDays ?? 0);
}

/** True si el bucket semanal/mensual solapa con [calculationDate, calculationDate + protectionDays]. */
export function isWithinProtectionPeriod(
  periodStart: string,
  periodEnd: string,
  calculationDate: string | null | undefined,
  protectionDays: number,
): boolean {
  if (!calculationDate || protectionDays <= 0) return false;
  const windowStart = parseIsoDate(calculationDate);
  const windowEnd = new Date(windowStart);
  windowEnd.setDate(windowEnd.getDate() + protectionDays);
  const bucketStart = parseIsoDate(periodStart);
  const bucketEnd = parseIsoDate(periodEnd);
  return bucketStart <= windowEnd && bucketEnd >= windowStart;
}

export interface ProtectionPeriodOptions {
  calculationDate?: string | null;
  protectionPeriodDays?: number | null;
  leadTimeDays?: number | null;
  reviewPeriodDays?: number | null;
}

function rowToPeriod(
  row: ProjectionRow,
  key: string,
  label: string,
  inProtectionPeriod: boolean,
): PivotPeriod {
  return {
    key,
    label,
    periodStart: row.period_start,
    periodEnd: row.period_end,
    startingStock: toNum(row.starting_stock),
    confirmedReceipts: toNum(row.confirmed_receipts),
    delayedReceipts: delayedReceiptsFromDetails(row.receipts_details),
    suggestedReceipts: toNum(row.suggested_receipts),
    projectedDemand: toNum(row.projected_demand),
    endingStock: toNum(row.ending_stock),
    safetyStock: toNum(row.safety_stock),
    belowSafety: row.below_safety_stock,
    stockout: row.stockout,
    inProtectionPeriod,
  };
}

/**
 * Construye las columnas del pivote para un tipo de simulacion.
 * En mensual se agregan los flujos (llegadas, sugerido, demanda); el stock
 * inicial es el del primer periodo del mes y el final el del ultimo periodo.
 */
export function buildPivotPeriods(
  projections: ProjectionRow[],
  simulationType: SimulationType,
  granularity: Granularity,
  protectionOptions?: ProtectionPeriodOptions,
): PivotPeriod[] {
  const rows = projections
    .filter((p) => p.simulation_type === simulationType)
    .sort((a, b) => a.period_index - b.period_index);

  const protectionDays = protectionOptions
    ? resolveProtectionDays(
        protectionOptions.protectionPeriodDays,
        protectionOptions.leadTimeDays,
        protectionOptions.reviewPeriodDays,
      )
    : 0;
  const calculationDate = protectionOptions?.calculationDate ?? null;

  const inProtection = (periodStart: string, periodEnd: string) =>
    isWithinProtectionPeriod(periodStart, periodEnd, calculationDate, protectionDays);

  if (granularity === 'week') {
    return rows.map((row) =>
      rowToPeriod(
        row,
        `w-${row.period_index}`,
        isoWeekLabel(row.period_start),
        inProtection(row.period_start, row.period_end),
      ),
    );
  }

  const byMonth = new Map<string, ProjectionRow[]>();
  for (const row of rows) {
    const key = monthKey(row.period_start);
    const list = byMonth.get(key);
    if (list) list.push(row);
    else byMonth.set(key, [row]);
  }

  const periods: PivotPeriod[] = [];
  for (const [key, monthRows] of byMonth) {
    const first = monthRows[0];
    const last = monthRows[monthRows.length - 1];
    const confirmedReceipts = monthRows.reduce((acc, r) => acc + toNum(r.confirmed_receipts), 0);
    const delayedReceipts = monthRows.reduce(
      (acc, r) => acc + delayedReceiptsFromDetails(r.receipts_details),
      0,
    );
    const suggestedReceipts = monthRows.reduce((acc, r) => acc + toNum(r.suggested_receipts), 0);
    const projectedDemand = monthRows.reduce((acc, r) => acc + toNum(r.projected_demand), 0);
    const endingStock = toNum(last.ending_stock);
    const safetyStock = toNum(last.safety_stock);
    periods.push({
      key: `m-${key}`,
      label: monthLabel(first.period_start),
      periodStart: first.period_start,
      periodEnd: last.period_end,
      startingStock: toNum(first.starting_stock),
      confirmedReceipts,
      delayedReceipts,
      suggestedReceipts,
      projectedDemand,
      endingStock,
      safetyStock,
      belowSafety: monthRows.some((r) => r.below_safety_stock) || endingStock < safetyStock,
      stockout: monthRows.some((r) => r.stockout) || endingStock < 0,
      inProtectionPeriod: inProtection(first.period_start, last.period_end),
    });
  }
  return periods;
}
